import { FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { cn } from '@/styles'
import EyeIcon from './assets/eye.svg'
import EyeClose from './assets/eye-close.svg'
import { useMemo, useState } from 'react'
import { Textarea } from '../ui/textarea'

type BaseInputGroupProps = {
  Icon: React.FC<React.SVGProps<SVGSVGElement>>
  label: string
  value?: string
  placeholder?: string
  required?: boolean
  className?: string
  errmsg?: string
  // 只顯示錯誤樣式 (紅線)，不顯示錯誤訊息
  invalid?: boolean
  right?: React.ReactNode
  footer?: React.ReactNode
}

type InputGroupProps =
  | (BaseInputGroupProps & {
      type?: 'text' | 'password' | 'email' | 'number'
    } & React.ComponentProps<'input'>)
  | (BaseInputGroupProps & {
      type: 'textarea'
    } & React.ComponentProps<'textarea'>)

export const InputGroup = ({ Icon, label, value, placeholder, required, className, type = 'text', errmsg, invalid, right, footer, ...props }: InputGroupProps) => {
  const [eyeOpen, setEyeOpen] = useState(false)

  const Eye = useMemo(() => {
    return eyeOpen ? EyeIcon : EyeClose
  }, [eyeOpen])

  return (
    <>
      <div className={cn('flex shrink-0 items-center justify-between border-b', errmsg || invalid ? 'border-[#FF4343]' : 'border-[#EBEBEB]', type === 'textarea' ? 'h-auto' : 'h-44', className)}>
        {/* 左側內容 */}
        <FieldLabel className="flex flex-1 items-center gap-8">
          {Icon && <Icon className="mb-4 size-24 shrink-0 text-[#9DABC9]" />}

          <div className="flex w-full flex-col gap-4 sm:flex-row">
            <div className="flex leading-16 sm:shrink-0 sm:items-center">
              <span className="text-12 sm:text-16 mr-4 leading-14 font-normal text-[#0D2245] capitalize">{label}</span>
              {required && <span className="text-14 text-[#FF4343]">*</span>}
            </div>

            {type === 'textarea' ? (
              <Textarea className="text-12 sm:text-16 leading-14 font-normal text-[#0D2245]" placeholder={placeholder} value={value} {...(props as React.ComponentProps<'textarea'>)} />
            ) : (
              <Input
                className="text-12 sm:text-16 px-0 py-0 pb-6 leading-14 font-normal text-[#0D2245]"
                placeholder={placeholder}
                type={eyeOpen ? 'text' : type}
                value={value}
                autoComplete="off"
                {...(props as React.ComponentProps<'input'>)}
              />
            )}
          </div>
        </FieldLabel>

        {/* 右側內容 */}
        {(type === 'password' || right) && (
          <div className="pr-8 select-none">
            {type === 'password' && !right && (
              <div onClick={() => setEyeOpen(!eyeOpen)}>
                <Eye className="text-[#6D7693]" />
              </div>
            )}

            {/* 客制化內容 */}
            {right}
          </div>
        )}
      </div>

      {/* 錯誤訊息 & Footer(客制化內容) */}
      {(errmsg || footer) && (
        <div className="mt-8 flex items-start justify-between">
          <div className="text-12 sm:text-16 text-[#FF4343] capitalize">{errmsg}</div>
          <div>{footer}</div>
        </div>
      )}
    </>
  )
}
